import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Milestone,
  Link2,
  DollarSign,
  FlaskConical,
  FileCheck,
  Users,
  Calendar,
  MessageSquare,
  ExternalLink,
  Eye,
  Building2,
  MapPin,
  Loader2,
  Pin,
} from 'lucide-react';
import api from '../lib/api';

const POST_TYPE_CONFIG = {
  pipeline_milestone: { icon: Milestone, color: 'purple', label: 'Pipeline Milestone' },
  partnership_news: { icon: Link2, color: 'blue', label: 'Partnership News' },
  fundraising: { icon: DollarSign, color: 'green', label: 'Fundraising' },
  clinical_data: { icon: FlaskConical, color: 'orange', label: 'Clinical Data' },
  regulatory: { icon: FileCheck, color: 'red', label: 'Regulatory Update' },
  team_update: { icon: Users, color: 'indigo', label: 'Team Update' },
  conference: { icon: Calendar, color: 'pink', label: 'Conference' },
  general: { icon: MessageSquare, color: 'gray', label: 'General Update' },
};

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/posts/${id}`);
        setPost(response.data.data);

        // Track view
        try {
          await api.post(`/posts/${id}/view`);
        } catch (e) {
          // Silent fail for view tracking
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-700',
      blue: 'bg-blue-100 text-blue-700',
      green: 'bg-green-100 text-green-700',
      orange: 'bg-orange-100 text-orange-700',
      red: 'bg-red-100 text-red-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      pink: 'bg-pink-100 text-pink-700',
      gray: 'bg-gray-100 text-gray-700',
    };
    return colors[color] || colors.gray;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-purple-600 hover:text-purple-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (!post) return null;

  const typeConfig = POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.general;
  const Icon = typeConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Post Header */}
          <div className="p-6 sm:p-8 border-b border-gray-100">
            <div className="flex items-start gap-4 mb-4">
              <div className={`p-3 rounded-xl ${getColorClasses(typeConfig.color)}`}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${getColorClasses(typeConfig.color)}`}>
                    {typeConfig.label}
                  </span>
                  {post.is_pinned && (
                    <span className="text-xs text-purple-600 flex items-center gap-1 bg-purple-50 px-2 py-1 rounded-full">
                      <Pin className="w-3 h-3" /> Pinned
                    </span>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {post.title}
                </h1>
                <p className="text-gray-500">
                  {formatDate(post.published_at || post.created_at)}
                </p>
              </div>
            </div>

            {/* Company Info */}
            <Link
              to={`/company/${post.seller_id}`}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors mt-4"
            >
              {post.logo_url ? (
                <img
                  src={post.logo_url}
                  alt={post.company_name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
              )}
              <div>
                <p className="font-semibold text-gray-900">{post.company_name}</p>
                {post.headquarters && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {post.headquarters}
                  </p>
                )}
              </div>
            </Link>
          </div>

          {/* Post Content */}
          <div className="p-6 sm:p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {post.content}
              </p>
            </div>

            {/* External Link */}
            {post.external_url && (
              <a
                href={post.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Read more on external site
              </a>
            )}

            {/* Extracted Signals */}
            {post.extracted_signals?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Related Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {post.extracted_signals.map((signal, idx) => (
                    <span
                      key={idx}
                      className="text-sm px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full"
                    >
                      {signal.value}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Stats */}
          <div className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {post.view_count || 0} views
            </span>
            {post.unique_viewer_count > 0 && (
              <span>{post.unique_viewer_count} unique viewers</span>
            )}
          </div>
        </article>

        {/* Therapeutic Areas */}
        {post.therapeutic_areas?.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Therapeutic Areas</h3>
            <div className="flex flex-wrap gap-2">
              {post.therapeutic_areas.map((area, idx) => (
                <span
                  key={idx}
                  className="text-sm px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
